import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { RunTag } from "@/components/RunTag";

export default function DropItem() {
  const { id } = useParams();
  const nav = useNavigate();
  const [item, setItem] = useState(null);
  const [tier, setTier] = useState(null);
  const [activeImageIdx, setActiveImageIdx] = useState(0);

  useEffect(() => {
    api
      .get(`/drop/${id}`)
      .then((res) => {
        setItem(res.data);
        setTier(res.data.tiers[0]?.code);
        setActiveImageIdx(0);
      })
      .catch(() => nav("/"));
  }, [id, nav]);

  if (!item) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="flex h-[60vh] items-center justify-center font-mono-stamp text-xs text-[#a0a0a0]">
          LOADING…
        </div>
      </div>
    );
  }

  const gallery = (item.gallery && item.gallery.length > 0) ? item.gallery : [item.image];
  const activeImage = gallery[activeImageIdx] || item.image;

  const activeTier = item.tiers.find((t) => t.code === tier) || item.tiers[0];
  const remaining = item.run_size - item.run_sold;
  const soldOut = remaining <= 0;

  return (
    <div data-testid="drop-item-page" className="min-h-screen">
      <Navbar />
      <section className="mx-auto max-w-[1400px] px-6 pb-24 pt-28 md:px-10">
        <Link
          to="/"
          className="font-mono-stamp text-xs text-[#a0a0a0] hover:text-white"
        >
          ← BACK TO THE DROP
        </Link>

        <div className="mt-8 grid grid-cols-12 gap-10">
          {/* Image + gallery */}
          <div className="col-span-12 md:col-span-7">
            <div className="relative border border-[#333] bg-[#f4f4f4]">
              <img
                src={activeImage}
                alt={item.name}
                data-testid="item-hero-image"
                className="aspect-[4/5] w-full object-cover"
              />
              <div className="absolute top-5 left-5">
                <RunTag
                  designNo={item.design_no}
                  runNumber={`${String(item.run_sold + 1).padStart(3, "0")}/${item.run_size}`}
                />
              </div>
            </div>

            {gallery.length > 1 && (
              <div
                data-testid="item-gallery"
                className="mt-3 grid grid-cols-4 gap-2 md:grid-cols-8"
              >
                {gallery.map((src, i) => (
                  <button
                    key={src}
                    type="button"
                    data-testid={`item-thumb-${i}`}
                    onClick={() => setActiveImageIdx(i)}
                    className={`aspect-square overflow-hidden border-2 bg-[#f4f4f4] transition-colors ${
                      activeImageIdx === i
                        ? "border-white"
                        : "border-[#333] hover:border-[#666]"
                    }`}
                  >
                    <img
                      src={src}
                      alt={`${item.name} view ${i + 1}`}
                      className="h-full w-full object-cover"
                      draggable={false}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="col-span-12 md:col-span-5">
            <p className="font-mono-stamp text-xs text-[#a0a0a0]">
              ▸ {item.category} · DESIGN {item.design_no}
            </p>
            <h1
              data-testid="item-name"
              className="font-display mt-4 text-6xl leading-[0.88] md:text-7xl"
            >
              {item.name.toUpperCase()}
            </h1>
            <p className="mt-4 font-display-medium text-xl text-[#a0a0a0] md:text-2xl">
              {item.tagline}
            </p>

            {/* Run status */}
            <div className="mt-8 border border-[#333] p-5">
              <div className="flex items-end justify-between">
                <div>
                  <p className="font-mono-stamp text-[10px] text-[#a0a0a0]">
                    ▸ RUN STATUS
                  </p>
                  <p className="font-display mt-1 text-3xl">
                    {item.run_sold} / {item.run_size}
                  </p>
                </div>
                <p className="font-mono-stamp text-xs text-white">
                  {soldOut
                    ? "SOLD OUT"
                    : remaining <= 10
                      ? "FINAL PIECES"
                      : `${remaining} REMAIN`}
                </p>
              </div>
              <div className="mt-3 h-[2px] w-full bg-[#222]">
                <div
                  className="h-full bg-white"
                  style={{
                    width: `${Math.round((item.run_sold / item.run_size) * 100)}%`,
                  }}
                />
              </div>
            </div>

            {/* Tier selector */}
            <div className="mt-8">
              <p className="font-mono-stamp text-xs text-[#a0a0a0]">
                ▸ QUALITY / PRICE TIER
              </p>
              <div className="mt-3 flex flex-col border border-[#333]">
                {item.tiers.map((t) => {
                  const active = tier === t.code;
                  return (
                    <button
                      key={t.code}
                      type="button"
                      data-testid={`item-tier-${t.code}`}
                      onClick={() => setTier(t.code)}
                      className={`flex items-center justify-between border-b border-[#333] px-4 py-3 text-left transition-colors last:border-b-0 ${active ? "bg-white text-black" : "hover:bg-[#0f0f0f]"}`}
                    >
                      <div>
                        <p className="font-display text-2xl">
                          {t.code} · ${t.price}
                        </p>
                        <p
                          className={`mt-1 font-mono-stamp text-[11px] ${active ? "text-black/60" : "text-[#a0a0a0]"}`}
                        >
                          {t.fabric} / {t.origin}
                        </p>
                      </div>
                      <span className="font-mono-stamp text-xs">
                        {active ? "● SELECTED" : "SELECT"}
                      </span>
                    </button>
                  );
                })}
              </div>
              <p className="mt-3 font-mono-stamp text-[10px] text-[#a0a0a0]">
                {activeTier.note}
              </p>
            </div>

            <div className="mt-10 flex flex-col gap-4">
              <button
                type="button"
                onClick={() =>
                  nav(`/checkout/${item.id}?tier=${activeTier.code}`)
                }
                data-testid="item-buy-cta"
                disabled={soldOut}
                className="btn-brutal btn-brutal-solid w-full justify-center disabled:opacity-40"
              >
                {soldOut
                  ? "SOLD OUT"
                  : `BUY — $${activeTier.price} ${activeTier.fabric.toUpperCase()} →`}
              </button>
              <Link
                to="/find-your-fit"
                data-testid="item-fyf-cta"
                className="btn-brutal w-full justify-center"
              >
                DON'T KNOW YOUR SIZE? FIND YOUR FIT
              </Link>
            </div>

            <p className="mt-8 font-mono-stamp text-[10px] leading-relaxed text-[#555]">
              EVERY PIECE HAND-STAMPED WITH A UNIQUE RUN TAG. NEVER REMADE.
              SHIPPING WORLDWIDE. 30-DAY RUN GUARANTEE.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
